/**
 * ACTIVE PERMISSION ENGINE – DO NOT MODIFY
 * Centralized permission utility for ERP. Used by all modules (Sales, Purchases, POS, Studio, Accounting, Inventory, etc.).
 * permissions (UserPermissions) are derived from role_permissions in SettingsContext.
 * Backend: has_permission(module, action) in DB; RLS uses same role_permissions.
 * UI: checkPermission(permissions, module, action) or useCheckPermission() hook.
 */

export type PermissionAction = 'view' | 'create' | 'edit' | 'delete' | 'cancel' | 'use';

export interface UserPermissions {
  role: 'Admin' | 'Manager' | 'Staff';
  canCreateSale: boolean;
  canEditSale: boolean;
  canDeleteSale: boolean;
  /** Cancel invoice: Admin + Manager only (Cashier cannot cancel) */
  canCancelSale?: boolean;
  canViewReports: boolean;
  canManageSettings: boolean;
  canManageUsers: boolean;
  canAccessAccounting: boolean;
  canMakePayments: boolean;
  canReceivePayments: boolean;
  canManageExpenses: boolean;
  canManageProducts: boolean;
  canManagePurchases: boolean;
  canManageRentals: boolean;
  canEditPurchase?: boolean;
  canDeletePurchase?: boolean;
  /** POS access from role_permissions (pos.use / pos.view) */
  canUsePos?: boolean;
  /** Studio Production access from role_permissions (studio.view / create / edit / delete) */
  canAccessStudio?: boolean;
  /** Sales view-only from role_permissions (view_own / view_branch / view_company) */
  canViewSale?: boolean;
  /** Contacts from role_permissions (contacts.view, create, edit, delete) */
  canViewContacts?: boolean;
  canCreateContact?: boolean;
  canDeleteContact?: boolean;
}

/** Module names for permission checks (must align with role_permissions.module where applicable) */
export type PermissionModule =
  | 'sales'
  | 'purchases'
  | 'pos'
  | 'studio'
  | 'rentals'
  | 'reports'
  | 'settings'
  | 'users'
  | 'accounting'   // role_permissions: ledger
  | 'payments'
  | 'expenses'     // role_permissions: payments (receive)
  | 'products'    // role_permissions: inventory
  | 'contacts'
  | 'inventory';

/**
 * Check if user has permission for given module + action.
 * Admin always has full access. Manager/Staff use granular flags.
 */
export function checkPermission(
  permissions: UserPermissions,
  module: PermissionModule,
  action: PermissionAction
): boolean {
  if (!permissions) return false;

  // Admin bypass - full access
  if (permissions.role === 'Admin') return true;

  switch (module) {
    case 'sales':
      if (action === 'view') return (permissions.canViewSale ?? false) || permissions.canCreateSale || permissions.canEditSale || permissions.canDeleteSale || (permissions.canCancelSale ?? false);
      if (action === 'create') return permissions.canCreateSale;
      if (action === 'edit') return permissions.canEditSale;
      if (action === 'delete') return permissions.canDeleteSale;
      if (action === 'cancel') return permissions.canCancelSale ?? false;
      return false;

    case 'purchases':
      if (action === 'view') return permissions.canManagePurchases === true;
      if (action === 'create') return permissions.canManagePurchases === true;
      if (action === 'edit') return permissions.canEditPurchase ?? permissions.canManagePurchases === true;
      if (action === 'delete') return permissions.canDeletePurchase ?? false; // Delete restricted by default
      return false;

    case 'pos':
      return (action === 'view' || action === 'use') && permissions.canUsePos === true;

    case 'studio':
      return (action === 'view' || action === 'create' || action === 'edit' || action === 'delete') && permissions.canAccessStudio === true;

    case 'rentals':
      return (action === 'view' || action === 'create' || action === 'edit' || action === 'delete') && permissions.canManageRentals === true;

    case 'reports':
      return action === 'view' && permissions.canViewReports === true;

    case 'settings':
      return permissions.canManageSettings === true;

    case 'users':
      return permissions.canManageUsers === true;

    case 'accounting':
      // view: anyone with accounting access; create/edit (manual entry, reversal, add account, pay courier): Admin and Manager only
      if (action === 'view') return permissions.canAccessAccounting === true;
      if (action === 'create' || action === 'edit') return permissions.canAccessAccounting === true && permissions.role === 'Manager';
      return false;

    case 'payments':
      if (action === 'view') return permissions.canMakePayments || permissions.canReceivePayments;
      if (action === 'create') return permissions.canMakePayments || permissions.canReceivePayments;
      return permissions.canMakePayments || permissions.canReceivePayments;

    case 'expenses':
      return permissions.canManageExpenses === true;

    case 'products':
      return permissions.canManageProducts === true;

    case 'contacts':
      if (action === 'view' || action === 'edit') return permissions.canViewContacts === true;
      if (action === 'create') return (permissions.canCreateContact ?? permissions.canViewContacts) === true;
      if (action === 'delete') return permissions.canDeleteContact === true;
      return false;

    case 'inventory':
      return action === 'view' && permissions.canManageProducts === true;

    default:
      return false;
  }
}

/** Convenience: can user edit sales? */
export function canEditSale(p: UserPermissions): boolean {
  return checkPermission(p, 'sales', 'edit');
}

/** Convenience: can user delete sales? */
export function canDeleteSale(p: UserPermissions): boolean {
  return checkPermission(p, 'sales', 'delete');
}

/** Convenience: can user cancel invoice? (Admin + Manager; Cashier cannot) */
export function canCancelSale(p: UserPermissions): boolean {
  return checkPermission(p, 'sales', 'cancel');
}

/** Convenience: can user delete purchases? (restricted) */
export function canDeletePurchase(p: UserPermissions): boolean {
  return checkPermission(p, 'purchases', 'delete');
}

/** Convenience: can user view financial reports? */
export function canViewReports(p: UserPermissions): boolean {
  return checkPermission(p, 'reports', 'view');
}

